import qs from 'query-string';

const isObject = v => v && typeof v === 'object' && !Array.isArray(v);

function tryFormatDateRange(obj) {
  if (!isObject(obj)) return null;
  const { start_date, end_date } = obj;
  if (start_date || end_date) {
    return `${start_date ?? ''}${start_date && end_date ? ' → ' : ''}${end_date ?? ''}`.trim();
  }
  return null;
}

function pickBestStringFromObject(obj) {
  if (!isObject(obj)) return null;
  const candidates = ['fullLabel', 'label', 'name', 'title'];
  for (const k of candidates) {
    if (obj[k]) return String(obj[k]);
  }

  const primitiveParts = Object.keys(obj)
    .filter(k => ['string', 'number', 'boolean'].includes(typeof obj[k]))
    .map(k => `${k}: ${obj[k]}`);
  if (primitiveParts.length) return primitiveParts.join(', ');
  return null;
}

function smartLabelForValue(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map(v => smartLabelForValue(v)).join(', ');
  const dateRange = tryFormatDateRange(val);
  if (dateRange) return dateRange;
  const best = pickBestStringFromObject(val);
  if (best) return best;
  try {
    return JSON.stringify(val);
  } catch (e) {
    return String(val);
  }
}

export const parseQueryParams = search => {
  const parsed = qs.parse(search);
  if (!parsed.filters) return {};
  try {
    return JSON.parse(decodeURIComponent(parsed.filters));
  } catch {
    return {};
  }
};

export const normalize = filters => {
  const normalized = {};
  if (!filters || typeof filters !== 'object') return normalized;

  Object.entries(filters).forEach(([key, value]) => {
    if (!Array.isArray(value)) return;
    if (key === 'topic_filters') {
      normalized[key] = value.map(andGroup => andGroup.map(item => item?.fullValue ?? item?.value ?? item));
    } else {
      normalized[key] = value.map(v => v?.fullValue ?? v?.value ?? v);
    }
  });

  return normalized;
};

export function updateFilterValues(filters, config) {
  const filterConfig = Array.isArray(config) ? config : [];

  const flattenOptions = options => {
    if (!Array.isArray(options)) return [];
    const out = [];
    const stack = [...options];
    while (stack.length) {
      const cur = stack.pop();
      out.push(cur);
      if (Array.isArray(cur.children) && cur.children.length) stack.push(...cur.children);
    }
    return out;
  };

  const findMatchingOption = (options, value) => {
    if (!Array.isArray(options)) return null;
    const flat = flattenOptions(options);
    for (const opt of flat) {
      const optVal = opt.fullValue ?? opt.value;
      if (isObject(optVal) && isObject(value)) {
        try {
          if (JSON.stringify(optVal) === JSON.stringify(value)) return opt;
        } catch (e) {}
      } else {
        if (optVal === value) return opt;
      }
    }
    return null;
  };

  const updated = {};
  if (!filters || typeof filters !== 'object') return updated;

  Object.entries(filters).forEach(([key, filterValue]) => {
    if (key === 'topic_filters' && Array.isArray(filterValue)) {
      const configItem = filterConfig.find(f => f.key === key);
      const options = configItem?.options ?? [];

      updated[key] = filterValue.map(andGroup =>
        (andGroup || []).map(item => {
            const rawVal = item?.fullValue ?? item?.value ?? item;

            const match = findMatchingOption(options, rawVal);
            if (match) {
              const fullValue = match.fullValue ?? match.value ?? rawVal;
              const label = match.label ?? smartLabelForValue(match);
              const fullLabel = match.fullLabel ?? match.label ?? smartLabelForValue(match);

              return {
                label,
                value: match.value ?? rawVal,
                fullValue,
                fullLabel
              };
            }
            const smart = smartLabelForValue(rawVal);
            return {
              label: smart,
              value: rawVal,
              fullValue: rawVal,
              fullLabel: smart
            };
          })
        );
    } else if (Array.isArray(filterValue)) {
      const configItem = filterConfig.find(f => f.key === key);
      const options = configItem?.options ?? [];
      updated[key] = filterValue.map(item => {
        const val = item?.fullValue ?? item?.value ?? item;
        const match = findMatchingOption(options, val);
        if (match) {
          return {
            label: match.label ?? smartLabelForValue(match),
            value: match.value ?? val,
            fullValue: match.fullValue ?? match.value ?? val,
            fullLabel: match.fullLabel ?? match.label ?? smartLabelForValue(match)
          };
        }
        const smart = smartLabelForValue(val);
        return { label: smart, value: val, fullValue: val, fullLabel: smart };
      });
    } else {
      const configItem = filterConfig.find(f => f.key === key);
      const options = configItem?.options ?? [];
      const val = filterValue;
      const match = findMatchingOption(options, val);
      if (match) {
        updated[key] = [
          {
            label: match.label ?? smartLabelForValue(match),
            value: match.value ?? val,
            fullValue: match.fullValue ?? match.value ?? val,
            fullLabel: match.fullLabel ?? match.label ?? smartLabelForValue(match)
          }
        ];
      } else {
        const smart = smartLabelForValue(val);
        updated[key] = [{ label: smart, value: val, fullValue: val, fullLabel: smart }];
      }
    }
  });

  return updated;
}
